/**
 * OnboardingModal Component
 * Welcome modal for new users with quick feature overview
 */

import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { useOnboardingContext } from './OnboardingProvider';
import { dashboardTour } from '@/config/onboardingSteps';

interface OnboardingModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * OnboardingModal Component
 */
export const OnboardingModal: React.FC<OnboardingModalProps> = ({ open, onOpenChange }) => {
  const { startTour, dismissWelcome, skipTour } = useOnboardingContext();

  /**
   * Start the dashboard tour
   */
  const handleStartTour = () => {
    dismissWelcome();
    onOpenChange(false);
    startTour(dashboardTour.id);
  };

  /**
   * Skip the tour
   */
  const handleSkipTour = () => {
    dismissWelcome();
    skipTour(dashboardTour.id);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          {/* Logo or Brand Mark */}
          <div className="flex items-center justify-center mb-6">
            <div className="relative">
              {/* Swiss Shield Icon */}
              <svg
                className="w-16 h-16 text-terracotta-600"
                viewBox="0 0 64 64"
                fill="currentColor"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M32 2L8 12v16c0 14.4 9.92 27.84 24 31.2 14.08-3.36 24-16.8 24-31.2V12L32 2z" />
                <rect x="24" y="20" width="16" height="4" fill="white" />
                <rect x="28" y="16" width="8" height="20" fill="white" />
              </svg>
            </div>
          </div>

          <DialogTitle className="text-2xl font-serif text-center">
            Welcome to LegalSyncUp
          </DialogTitle>
          <DialogDescription className="text-center text-base">
            Your Swiss legal entity management platform
          </DialogDescription>
        </DialogHeader>

        <div className="py-6">
          {/* Feature highlights */}
          <div className="space-y-4">
            <FeatureCard
              icon={
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                  />
                </svg>
              }
              title="Manage Swiss Entities"
              description="Organize all your Swiss legal entities (AG, GmbH, Verein, etc.) with full UID validation and compliance tracking."
            />

            <FeatureCard
              icon={
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                  />
                </svg>
              }
              title="Document Workflows"
              description="Upload, version, and approve legal documents with Swiss legal role templates and audit trails."
            />

            <FeatureCard
              icon={
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
              }
              title="Team Collaboration"
              description="Invite team members with role-based permissions for secure, compliant collaboration."
            />

            <FeatureCard
              icon={
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                  />
                </svg>
              }
              title="Swiss Compliance"
              description="Built-in compliance features for Swiss legal requirements with cantonal registration tracking."
            />
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <button
            onClick={handleSkipTour}
            className="px-6 py-2.5 text-sm font-medium text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-espresso-700 rounded-md transition-colors"
          >
            Skip Tour
          </button>
          <button
            onClick={handleStartTour}
            className="px-6 py-2.5 text-sm font-medium text-white bg-terracotta-600 hover:bg-terracotta-700 rounded-md transition-colors shadow-sm"
          >
            Start Tour
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Feature Card Component
 */
interface FeatureCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
}

const FeatureCard: React.FC<FeatureCardProps> = ({ icon, title, description }) => {
  return (
    <div className="flex gap-4 p-4 rounded-lg bg-cream-100 dark:bg-espresso-700/30 transition-colors hover:bg-cream-200 dark:hover:bg-espresso-700/50">
      <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-md bg-terracotta-100 dark:bg-terracotta-900/30 text-terracotta-700 dark:text-terracotta-400">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="text-sm font-semibold text-espresso-900 dark:text-cream-100 mb-1">
          {title}
        </h4>
        <p className="text-xs text-neutral-600 dark:text-neutral-300 leading-relaxed">
          {description}
        </p>
      </div>
    </div>
  );
};
